import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@/lib/utils';
import Modal from '@/components/modal/Modal.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import ModalHeader from '@/components/modal/ModalHeader.tsx';
import ModalFooter from '@/components/modal/ModalFooter.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import CacPhoneInput from '@/components/form/CacPhoneInput.tsx';
import CheckboxInput from '@/components/form/CheckboxInput.tsx';
import { DateInput } from '@/components/form/DateInput.tsx';
import MultiSelectInput from '@/components/form/MultiSelectInput.tsx';
import { NumberInput } from '@/components/form/NumberInput.tsx';
import SingleSelectInput from '@/components/form/SingleSelectInput.tsx';
import { TextInput } from '@/components/form/TextInput.tsx';
import { TextareaInput } from '@/components/form/TextareaInput.tsx';
import ToggleInput from '@/components/form/ToggleInput.tsx';
import Option from '@/components/form/types/Option.ts';
import { FieldType } from '@/components/table/_fieldDefinitions/FieldType.ts';
import { currencySelectOptions } from '@/features/dashboard/paymentConfig/types/Currency.utils.ts';
import { formatMoneyList } from '@/features/dashboard/bookingChanges/utils/formatters.ts';
import { BillField } from '@/features/dashboard/bookingChanges/types/dto/BillField.ts';
import { BillRowVm } from '@/features/dashboard/bookingChanges/types/BillViewModel.ts';
import { CatalogRefDto } from '@/features/dashboard/availability/types/AvailabilityDto.ts';
import useCatalogAvailability from '@/features/dashboard/availability/hooks/useCatalogAvailability.ts';
import SeatLimitNotice from '@/features/dashboard/availability/components/SeatLimitNotice.tsx';
import { CHANGE_TOGGLE_OPTIONS, editFieldTypeLabel } from './editFieldCopy';
import {
  buildEditFieldSchema,
  EditFieldFormData,
  perFieldInputs,
} from './editFieldSchema';
import { EMPTY_VALUE } from '@/components/ui/EmptyValue.tsx';

interface EditFieldModalProps {
  row: BillRowVm;
  /** Nazwa sekcji, z której przyszedł wiersz — podtytuł okna. */
  sectionTitle: string;
  type: FieldType;
  /** Pola kontraktu, które edytuje wiersz — więcej niż jedno znaczy input na każde. */
  fields: BillField[];
  /** Wartości dopuszczalne dla tego wiersza — grupa katalogu, nie cały cennik. */
  options: Option[];
  tourId: string;
  /** Warianty, o których obłożenie pytamy; pusta lista = wiersz bez limitu miejsc. */
  seatRefs: CatalogRefDto[];
  /** Wiersz niesie kwotę — pokazujemy blok ręcznego nadpisania ceny. */
  withAmount: boolean;
  defaultValues: EditFieldFormData;
  /** Pozycja ręczna z przycisku „+ Zniżka” — podpowiadamy kwotę ujemną. */
  discount?: boolean;
  isSaving: boolean;
  onSubmit: (values: EditFieldFormData) => void;
  /** Zdjęcie pozycji ze szkicu; brak = pozycji nie da się usunąć. */
  onRemove?: () => void;
  onClose: () => void;
}

/**
 * Wiersz wybierany z listy zawsze ma listę — pusta znaczy, że wyjazd nie ma tu
 * czego zaoferować (opcja bez wariantów, transport bez tras, pole listy bez
 * opcji), a nie że wartości nie znamy. Wpisu ręcznego nie ma: wartością jest
 * identyfikator z katalogu i tekst nie miałby jak nim zostać.
 */
const EMPTY_OPTIONS_HINT = 'Wyjazd nie ma tu żadnej wartości do wyboru.';

/** Znak kwoty jest tu całą różnicą między kosztem a zniżką — mówimy to wprost. */
const MANUAL_AMOUNT_HINT =
  'Kwota dodatnia to koszt, ujemna to zniżka — np. -150.';

/**
 * Opis pozycji ręcznej **czyta zapisujący** — stoi na jego rachunku jako nazwa wiersza. To jedyne
 * pole tego okna, które wychodzi poza panel organizatora, więc mówimy o tym wprost; uwagi wewnętrzne
 * mają swoje miejsce w uzasadnieniu kwoty.
 */
const MANUAL_DESCRIPTION_HINT =
  'Ten opis zobaczy zapisujący na podsumowaniu rezerwacji.';

/**
 * Wartość, którą wiersz pokazuje dziś na rachunku.
 *
 * Wiersz kwotowy — cena bazowa, pozycja ręczna — nie ma wartości tekstowej:
 * jego wartością **jest** kwota. Bez tego okno edycji ceny pokazywało myślnik
 * nad polem, w którym ta sama kwota stała już wpisana.
 */
const currentValueOf = (row: BillRowVm): string =>
  row.chips?.map((chip) => chip.label).join(', ') ??
  row.value ??
  (row.amount.length > 0 ? formatMoneyList(row.amount) : null) ??
  row.amountLabel ??
  EMPTY_VALUE;

const summaryRowClass = 'flex items-baseline justify-between gap-3 text-sm';

/**
 * Formularz edycji pojedynczej pozycji szkicu.
 *
 * ⚠️ **Układ tego okna nie pochodzi z handoffu** — projekt wprost go nie
 * obejmuje („Nie są zaprojektowane: formularze edycji pojedynczego pola”).
 * To celowo oszczędna forma domykająca przepływ (kliknięcie „Zmień” → pozycja
 * w szkicu) i wymaga zaprojektowania, zanim trafi do klientów.
 *
 * Kontrolkę wybiera `FieldType` — ten sam słownik, którym mówi cała aplikacja
 * (`ParticipantCustomFieldsForm`). Ile pól wiersz edytuje, wynika z jego
 * `ref.fields`, a nie z typu: to kształt wiersza, nie rodzaj pola.
 */
const EditFieldModal = ({
  row,
  sectionTitle,
  type,
  fields,
  options,
  tourId,
  seatRefs,
  withAmount,
  defaultValues,
  discount = false,
  isSaving,
  onSubmit,
  onRemove,
  onClose,
}: EditFieldModalProps) => {
  const manual = row.manual ?? false;
  const shape = { type, fields, manual };
  const form = useForm<EditFieldFormData>({
    resolver: zodResolver(buildEditFieldSchema(shape)),
    defaultValues,
  });
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isDirty },
  } = form;

  const priceOverridden = useWatch({ control, name: 'priceOverridden' });
  const chosenValue = useWatch({ control, name: 'value' });
  const { byId: seatsByVariant } = useCatalogAvailability(tourId, seatRefs);
  // Ostrzegamy o tym, co operator właśnie wybrał, a nie o całej liście: wariant
  // z kompletem chętnych obok w rozwijanej nikomu nie przeszkadza.
  const chosenSeats =
    typeof chosenValue === 'string'
      ? seatsByVariant.get(chosenValue)
      : undefined;
  const emptyOptionsHint =
    options.length === 0 ? EMPTY_OPTIONS_HINT : undefined;
  const fieldInputs = perFieldInputs(fields);
  const typeLabel = editFieldTypeLabel(shape);
  // Kwota wiersza wielowartościowego jest sumą kilku wariantów — jedno pole
  // „kwota” nie miałoby do czego się przypiąć, więc nadpisania tu nie ma.
  // Pozycja ręczna odpada razem z nimi: nie ma ceny katalogowej, którą dałoby
  // się nadpisać, bo wpisana kwota *jest* całą jej ceną (stąd `MONEY_VALUE`).
  const showPriceOverride =
    withAmount &&
    type !== FieldType.MONEY_VALUE &&
    type !== FieldType.MULTISELECT;

  const renderControl = () => {
    // Pozycja ręczna jest jedynym wierszem, którego pola należą do niej samej,
    // a nie do osoby ani do cennika — opisu nie da się wziąć znikąd indziej, bo
    // pozycja nie ma nazwy ani w rachunku, ani w cenniku: nadaje ją operator.
    if (manual) {
      return (
        <>
          <TextInput
            label="Opis pozycji"
            placeholder="np. Rabat lojalnościowy"
            register={register('description')}
            hint={MANUAL_DESCRIPTION_HINT}
            error={errors.description?.message}
          />
          {/*
            Pozycja ręczna jako jedyna nie ma wiersza księgi, z którego dałoby
            się wziąć walutę — a rezerwacja bywa wielowalutowa, więc wskazuje
            ją operator. Pole stoi obok kwoty, bo bez niej nic nie znaczy.
          */}
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <NumberInput
                label="Kwota"
                placeholder={discount ? '-0,00' : '0,00'}
                step={0.01}
                register={register('value')}
                hint={MANUAL_AMOUNT_HINT}
                error={errors.value?.message}
              />
            </div>
            <div className="w-28 shrink-0">
              <SingleSelectInput
                fieldName="currency"
                label="Waluta"
                control={control}
                options={currencySelectOptions}
                error={errors.currency?.message}
              />
            </div>
          </div>
        </>
      );
    }

    if (fieldInputs.length > 0) {
      return fieldInputs.map((input) => (
        <TextInput
          key={input.name}
          label={input.label}
          placeholder={input.placeholder}
          register={register(input.name)}
          error={errors[input.name]?.message}
        />
      ));
    }

    switch (type) {
      case FieldType.DATE:
        return (
          <DateInput
            label="Nowa data"
            dateFieldName="date"
            control={control}
            error={errors.date?.message}
          />
        );

      case FieldType.CHECKBOX:
        return (
          <ToggleInput
            fieldPath="value"
            label="Nowa wartość"
            options={CHANGE_TOGGLE_OPTIONS}
            control={control}
            error={errors.value?.message}
          />
        );

      case FieldType.MONEY_VALUE:
        return (
          <NumberInput
            label="Nowa kwota"
            placeholder="0,00"
            step={0.01}
            register={register('value')}
            error={errors.value?.message}
          />
        );

      case FieldType.DROPDOWN:
        return (
          <SingleSelectInput
            fieldName="value"
            label="Nowa wartość"
            control={control}
            options={options}
            hint={emptyOptionsHint}
            error={errors.value?.message}
          />
        );

      case FieldType.MULTISELECT:
        // Wybór wielokrotny ma wyłącznie opcja wyjazdu, a każdy zaznaczony
        // wariant to osobna pozycja paczki.
        return (
          <MultiSelectInput
            fieldPath="values"
            label="Nowe wartości"
            control={control}
            options={options}
            hint={emptyOptionsHint}
            error={errors.values?.message}
          />
        );

      case FieldType.PHONE_NUMBER:
        return (
          <CacPhoneInput
            fieldPath="phoneNumber"
            label="Nowy numer telefonu"
            error={errors.phoneNumber?.message}
          />
        );

      case FieldType.LONG_TEXT:
        return (
          <TextareaInput
            label="Nowa wartość"
            register={register('value')}
            error={errors.value?.message}
          />
        );

      default:
        return (
          <TextInput
            label="Nowa wartość"
            register={register('value')}
            error={errors.value?.message}
          />
        );
    }
  };

  return (
    <Modal
      closeOnOverlayClick
      isDirty={isDirty}
      onClose={onClose}
      className="max-w-[520px] rounded-2xl"
    >
      <ModalHeader
        title={row.label}
        description={
          typeLabel ? `${sectionTitle} · ${typeLabel}` : sectionTitle
        }
        onClose={onClose}
      />

      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalContent className="px-6 py-5">
            <div className="flex flex-col gap-2 rounded-xl bg-neutral-50 px-4 py-3">
              <div className={summaryRowClass}>
                <span className="text-neutral-500">Wartość obecna</span>
                <span className="font-semibold text-ink">
                  {currentValueOf(row)}
                </span>
              </div>
              {row.oldValue && (
                <div className={cn(summaryRowClass, 'text-neutral-400')}>
                  <span>Sprzed zmiany</span>
                  <span className="line-through">{row.oldValue}</span>
                </div>
              )}
            </div>

            {renderControl()}

            {chosenSeats?.full && (
              <SeatLimitNotice availability={chosenSeats} />
            )}

            {showPriceOverride && (
              <div className="flex flex-col gap-3 border-t border-neutral-100 pt-4">
                <CheckboxInput
                  label="Nadpisz kwotę ręcznie"
                  hint="Pozycja dostanie znacznik ✎ przy kwocie."
                  register={register('priceOverridden')}
                />

                {priceOverridden && (
                  <>
                    <NumberInput
                      label="Kwota"
                      placeholder="0,00"
                      step={0.01}
                      register={register('price')}
                      error={errors.price?.message}
                    />
                    <TextInput
                      label="Uzasadnienie kwoty"
                      register={register('priceNote')}
                      hint="Zostaje u organizatora — trafia do historii paczki."
                    />
                  </>
                )}
              </div>
            )}

            {type === FieldType.MONEY_VALUE && (
              <TextInput
                label="Uzasadnienie kwoty"
                register={register('priceNote')}
                hint="Zostaje u organizatora — trafia do historii paczki."
              />
            )}
          </ModalContent>

          <ModalFooter>
            {onRemove && (
              <ActionButton
                tone="danger"
                size="sm"
                className="mr-auto"
                onClick={onRemove}
                disabled={isSaving}
              >
                Usuń pozycję
              </ActionButton>
            )}
            <ActionButton
              tone="outline"
              size="sm"
              onClick={onClose}
              disabled={isSaving}
            >
              Anuluj
            </ActionButton>
            <ActionButton
              tone="accent"
              size="sm"
              type="submit"
              isLoading={isSaving}
            >
              Zapisz w szkicu
            </ActionButton>
          </ModalFooter>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default EditFieldModal;
